import { useState } from "react";

import Button from "@/components/Button";
import ModalLayout from "@/components/Layout/ModalLayout";
import useModal from "@/hooks/useModal";
import MoveModal from "@/pages/Admin/components/OrgManagement/MoveModal";
import {
  Community,
  OrgUser,
} from "@/pages/Admin/components/OrgManagement/types";

// ---- 유저 목록 모달 ----

interface UserListModalProps {
  orgName: string;
  users: OrgUser[];
  communities: Community[];
  onMove: (
    userIds: string[],
    cmtCd: string,
    garCd: string,
    leafCd: string,
  ) => void;
  onClose: () => void;
}

const UserListModal = ({
  orgName,
  users,
  communities,
  onMove,
  onClose,
}: UserListModalProps) => {
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const moveModal = useModal();

  const getUserId = (u: OrgUser) => u.userId ?? u.mbrId ?? u.userName;

  const allChecked =
    users.length > 0 && users.every(u => checkedIds.has(getUserId(u)));

  const toggleAll = () => {
    if (allChecked) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(users.map(getUserId)));
    }
  };

  const toggleOne = (id: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleMoveConfirm = (cmtCd: string, garCd: string, leafCd: string) => {
    onMove([...checkedIds], cmtCd, garCd, leafCd);
    moveModal.onClose();
  };

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="flex w-[calc(100vw-32px)] flex-col sm:w-[560px]">
        {/* 헤더 */}
        <div className="flex h-20 items-center rounded-t-sm bg-primary px-6 text-xl font-light text-white">
          {orgName} 소속 인원
        </div>

        {/* 본문 */}
        <div className="max-h-[50vh] overflow-y-auto p-4">
          {users.length === 0 ? (
            <p className="py-4 text-center text-small text-gray-dull">
              소속 인원이 없습니다.
            </p>
          ) : (
            <table className="w-full text-small">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="border-b border-gray-200 px-3 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                      className="cursor-pointer"
                    />
                  </th>
                  <th className="border-b border-gray-200 px-3 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    이름
                  </th>
                  <th className="border-b border-gray-200 px-3 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    전화번호
                  </th>
                  <th className="border-b border-gray-200 px-3 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    권한
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map(u => {
                  const uid = getUserId(u);

                  return (
                    <tr
                      key={uid}
                      className="transition-all duration-150 hover:-translate-y-px hover:bg-blue-50/40 hover:shadow-md"
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={checkedIds.has(uid)}
                          onChange={() => toggleOne(uid)}
                          className="cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700">
                        {u.userName}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500">
                        {u.telNo ?? "-"}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500">
                        {u.roleId ?? "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 푸터 */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-4 py-3">
          <span className="text-small text-gray-dull">
            {checkedIds.size}명 선택됨
          </span>
          <Button
            variant="outlined"
            className="text-small"
            disabled={checkedIds.size === 0}
            onClick={moveModal.onOpen}
          >
            선택 이동
          </Button>
          <Button variant="primary" className="text-small" onClick={onClose}>
            닫기
          </Button>
        </div>
      </div>

      {moveModal.isOpen && (
        <MoveModal
          communities={communities}
          selectedUserIds={[...checkedIds]}
          onConfirm={handleMoveConfirm}
          onClose={moveModal.onClose}
        />
      )}
    </ModalLayout>
  );
};

export default UserListModal;
